import * as React from "react"
import {
  Outlet,
  HeadContent,
  Scripts,
  createRootRoute,
  useRouterState,
} from "@tanstack/react-router"
import { QueryClientProvider } from "@tanstack/react-query"
import appCss from "../styles.css?url"
import { ThemeProvider } from "@/lib/providers/ThemeProvider"
import { useConfig } from "@/hooks/use-config"
import { ImpersonationBanner } from "@/components/ImpersonationBanner"
import { Analytics } from "@/components/Analytics"
import {
  SITE_DESCRIPTION,
  SITE_NAME,
  THEME_COLOR,
  DEFAULT_OG_IMAGE,
  absoluteUrl,
  createTitle,
} from "@/lib/seo"
import { getRenderQueryClient } from "@/lib/query-client"
import { rootSsr } from "@/lib/guide-ssr"
import { MapToggleProvider } from "@/lib/contexts/map-toggle-context"

// @vis.gl/react-google-maps (~85 KB) only matters on the two map-bearing
// layouts, but a static import here dragged it into `main` — i.e. onto every
// marketing page that never shows a map. It's lazy so the cost lands only when
// we actually sticky-mount the provider on the first map route of a session.
// (TripMap statically imports <Map> from the same package, so it shares this
// chunk and the planner is unaffected.) See the bundle-perf work (June 2026).
const APIProvider = React.lazy(() =>
  import(`@vis.gl/react-google-maps`).then((m) => ({ default: m.APIProvider }))
)

export const Route = createRootRoute({
  // App is SPA (`defaultSsr: false`). A child can only SSR if no ancestor is
  // ssr:false, so the root opts the SSR'd URLs in per-pathname: full SSR for the
  // marketing pages, data-only for guide detail, SPA everywhere else. Without
  // this the leaves' own `ssr` flags would be overridden to false.
  ssr: rootSsr,
  head: () => ({
    meta: [
      {
        charSet: `utf-8`,
      },
      {
        name: `viewport`,
        content: `width=device-width, initial-scale=1`,
      },
      {
        // Agoda affiliate partner domain verification. Name-only tag, must be in
        // the raw HTML head of the homepage — emitted here via the SSR'd
        // document shell.
        name: `agd-partner-manual-verification`,
      },
      {
        title: createTitle(),
      },
      {
        name: `description`,
        content: SITE_DESCRIPTION,
      },
      {
        property: `og:title`,
        content: createTitle(),
      },
      {
        property: `og:description`,
        content: SITE_DESCRIPTION,
      },
      {
        property: `og:type`,
        content: `website`,
      },
      {
        property: `og:url`,
        content: absoluteUrl(`/`),
      },
      {
        property: `og:site_name`,
        content: SITE_NAME,
      },
      {
        property: `og:image`,
        content: DEFAULT_OG_IMAGE,
      },
      {
        property: `og:image:width`,
        content: `1200`,
      },
      {
        property: `og:image:height`,
        content: `630`,
      },
      {
        name: `twitter:card`,
        content: `summary_large_image`,
      },
      {
        name: `twitter:image`,
        content: DEFAULT_OG_IMAGE,
      },
      {
        name: `twitter:title`,
        content: createTitle(),
      },
      {
        name: `twitter:description`,
        content: SITE_DESCRIPTION,
      },
      {
        name: `theme-color`,
        content: THEME_COLOR,
      },
      {
        name: `mobile-web-app-capable`,
        content: `yes`,
      },
      {
        name: `apple-mobile-web-app-capable`,
        content: `yes`,
      },
      {
        name: `apple-mobile-web-app-status-bar-style`,
        content: `black-translucent`,
      },
    ],
    links: [
      {
        rel: `preconnect`,
        href: `https://fonts.googleapis.com`,
      },
      {
        rel: `preconnect`,
        href: `https://fonts.gstatic.com`,
        crossOrigin: `anonymous`,
      },
      // Preload the font CSS so the browser fetches it early but non-blocking.
      // The actual stylesheet links are injected by a script in RootDocument
      // so they don't sit in the critical render path.
      {
        rel: `preload`,
        as: `style`,
        href: `https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,400&display=swap`,
        crossOrigin: `anonymous`,
      },
      {
        rel: `preload`,
        as: `style`,
        href: `https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600;9..144,700&display=swap`,
        crossOrigin: `anonymous`,
      },
      // Preconnect to our R2 CDN and Unsplash so guide cover images resolve
      // their DNS before the browser encounters the img src.
      {
        rel: `preconnect`,
        href: `https://pub-34d6d2c0c5f74dc482a4c0f70a791c79.r2.dev`,
      },
      {
        rel: `preconnect`,
        href: `https://images.unsplash.com`,
      },
      {
        rel: `stylesheet`,
        href: appCss,
      },
      // NOTE: the PWA manifest is intentionally NOT here — it's injected at
      // end-of-body (see RootDocument) so it doesn't sit in the initial
      // critical request chain. PWA install only needs it post-load.
      {
        // Primary favicon (the browser-tab icon), generated from logo512 so it
        // matches the PWA / home-screen icon. .ico is the most broadly supported
        // tab-icon format; the PNG entries below cover higher-DPI / PWA uses.
        rel: `icon`,
        type: `image/x-icon`,
        href: `/favicon.ico`,
      },
      {
        rel: `icon`,
        type: `image/png`,
        sizes: `192x192`,
        href: `/logo192.png`,
      },
      {
        rel: `icon`,
        type: `image/png`,
        sizes: `512x512`,
        href: `/logo512.png`,
      },
      {
        rel: `apple-touch-icon`,
        href: `/logo512.png`,
      },
    ],
  }),
  shellComponent: RootDocument,
  // HeroUIProvider/ToastProvider intentionally do NOT live here. They (and the
  // ~heavy @heroui/react bundle) are only used by the trip/guide editors, so they
  // mount in the _authenticated / _with-optional-sidebar layouts instead. Keeping
  // the import out of the root chunk is what lets the marketing pages ship without
  // HeroUI on their critical path.
  component: RootComponent,
})

function RootComponent() {
  // useState so a server render creates its per-request cache exactly once.
  const [client] = React.useState(getRenderQueryClient)
  return (
    <QueryClientProvider client={client}>
      <ThemeProvider>
        <RootContent />
      </ThemeProvider>
    </QueryClientProvider>
  )
}

// Only the trip planner and the guide views ever render a Google Map. We match
// on the trip/guide path segment rather than the whole layout: `/trips/explore`
// lives under `_with-optional-sidebar` too, and gating on the layout made it
// boot the ~144 KiB Maps SDK (plus the runtime tile/init work) for a page that
// shows no map at all. Marketing (`_public`), login and signup never match.
// NOTE: `/trips/` (the explore hub) deliberately does NOT contain `/trip/`, so
// the substring test excludes it while still catching `/trip/$tripId` etc.
function routeNeedsMaps(matches: ReadonlyArray<{ routeId: string }>) {
  return matches.some(
    (m) => m.routeId.includes(`/trip/`) || m.routeId.includes(`/guide/`)
  )
}

function RootContent() {
  const { config } = useConfig()
  const onMapRoute = useRouterState({
    select: (s) => routeNeedsMaps(s.matches),
  })

  // The Maps loader used to mount on EVERY page because APIProvider sat
  // unconditionally at the root — so the marketing pages shipped ~144 KiB of
  // Google Maps JS they never use, on the critical path. We can't gate it
  // naively: @vis.gl's GoogleMapsApiLoader breaks if APIProvider unmounts→
  // remounts (the script initializes twice → "This page can't load Google Maps
  // correctly"). So we mount it STICKILY — off until the first time we land on a
  // map-bearing route, then on for the rest of the session. A cold load of a
  // marketing page (real visitors, crawlers, Lighthouse) never mounts it, so it
  // pays zero Maps cost; once mounted it never unmounts, so the double-loader bug
  // can't trigger. The single wrap flip on the first marketing→app navigation
  // only remounts the Outlet (which is changing routes anyway), while the
  // long-lived Analytics/banner sit outside it and are untouched.
  //
  // Starts FALSE (not onMapRoute) so it doubles as the SSR/hydration gate: the
  // server and the first client render always paint the Outlet directly, never
  // the apiKey-less placeholder below. That matters now that `_with-optional-
  // sidebar` routes (the SSR'd explore hub + guide article) are server-rendered
  // for SEO — routeNeedsMaps is true for them, so initializing to onMapRoute
  // rendered an empty <div> as the entire SSR body. The Maps SDK is a client
  // concern: the SSR'd subtrees render no <Map>, and TripPage gates its map
  // behind a loading state, so no <Map> ever renders in the pre-mount frame. The
  // effect then sticky-mounts APIProvider one render later.
  const [mapsMounted, setMapsMounted] = React.useState(false)
  React.useEffect(() => {
    if (onMapRoute) setMapsMounted(true)
  }, [onMapRoute])

  // Only mount APIProvider once we actually have the key. @vis.gl's loader boots
  // the Maps bootstrap exactly ONCE with whatever key it first sees and ignores
  // every later key change ("already loaded with different parameters … refresh
  // the page"). `useConfig` fetches /api/config asynchronously, so on a cold
  // deep-link onto a map route (e.g. a shared guide) the key is still undefined
  // on first render — mounting the provider with an empty key there permanently
  // poisons Maps for the page load ("This page can't load Google Maps
  // correctly").
  const apiKey = config?.googleMapsApiKey

  // On a map route we must not render the Outlet until the provider is ready:
  // @vis.gl's <Map> throws if rendered outside an <APIProvider>. So while the
  // key is still loading on a map route, hold a neutral placeholder; the route
  // (and its map) then mounts exactly once, already inside the provider with the
  // real key. This gap only happens on the very first map page of a session
  // (config is cached after that), and never on marketing routes — which keep
  // mapsMounted=false and render the Outlet directly. The SSR'd reads (explore,
  // guide article) render server-side through the `!mapsMounted` branch, so
  // crawlers get the real HTML regardless of this client-only Maps gate.
  let content: React.ReactNode
  if (!mapsMounted) {
    content = <Outlet />
  } else if (apiKey) {
    content = (
      // APIProvider is lazy (keeps the Maps SDK wrapper out of `main`); hold the
      // same neutral placeholder used while the key loads until its chunk lands.
      <React.Suspense fallback={<div className="min-h-svh bg-background" />}>
        <APIProvider apiKey={apiKey}>
          <Outlet />
        </APIProvider>
      </React.Suspense>
    )
  } else {
    content = <div className="min-h-svh bg-background" />
  }

  // MapToggleProvider sits above every Outlet (and therefore every SiteHeader),
  // so both the authenticated shell and the logged-out public guide-read layout
  // can publish/read the mobile map/list toggle config across the header↔content
  // boundary. See map-toggle-context.tsx.
  return (
    <>
      <Analytics />
      <MapToggleProvider>{content}</MapToggleProvider>
      <ImpersonationBanner />
    </>
  )
}

const FONT_URLS = [
  `https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,400&display=swap`,
  `https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600;9..144,700&display=swap`,
]

// Fonts are applied EARLY (inline in <head>, right after the preloads) but
// NON-blocking, via the media="print"→onload="all" swap: the stylesheet starts
// downloading immediately (off the already-preloaded resource) yet never sits in
// the render-blocking set, so FCP is untouched. This matters for LCP: the hero
// <h1> is the largest paint, and waiting for requestIdleCallback to attach the
// fonts (which fires several seconds late under Lighthouse's throttled mobile
// CPU) made the heading re-paint into Poppins ~8s in, tanking LCP. Applying the
// font in parallel with the initial load pulls that final paint forward.
// NOTE: the onload uses `this` (the <link>), not a captured variable — the URLs
// share one function scope, so a closed-over `var l` would point every handler
// at the last link and leave the others stuck on media="print" (never applied).
const FONT_APPLY_SCRIPT = `(function(){${FONT_URLS.map(u =>
  `var l=document.createElement('link');l.rel='stylesheet';l.media='print';l.crossOrigin='anonymous';l.href='${u}';l.onload=function(){this.onload=null;this.media='all'};document.head.appendChild(l);`
).join(``)}})()`

// The PWA manifest is pushed out to requestIdleCallback so it never lands in the
// initial critical request chain (Lighthouse's "network dependency tree") — PWA
// install only needs it post-load.
const DEFERRED_HEAD_SCRIPT = `(function(){function addManifest(){var m=document.createElement('link');m.rel='manifest';m.href='/manifest.json';document.head.appendChild(m)}if('requestIdleCallback'in window){requestIdleCallback(addManifest,{timeout:4000})}else{setTimeout(addManifest,2000)}})()`

// Blocking, FIRST thing in <head>: resolve the theme and set the `.dark` class on
// <html> BEFORE the first paint, so SSR'd pages (which the server can only render
// in light mode — it can't read localStorage) don't flash light→dark for dark-mode
// users. Mirrors ThemeProvider exactly: storage key `theme`, values light|dark|
// system, system resolved via prefers-color-scheme. Tiny + synchronous by design;
// ThemeProvider's effect later just re-confirms what this set. <html> carries
// suppressHydrationWarning so React ignores the class this injects pre-hydration.
const THEME_INIT_SCRIPT = `(function(){try{var t=localStorage.getItem('theme')||'system';var d=t==='dark'||(t==='system'&&window.matchMedia('(prefers-color-scheme: dark)').matches);document.documentElement.classList.toggle('dark',d)}catch(e){}})()`

function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        {/* Runs before paint — kills the SSR light/dark flash. Keep it first. */}
        <script dangerouslySetInnerHTML={{ __html: THEME_INIT_SCRIPT }} />
        <HeadContent />
        {/* Attach the (preloaded) web fonts immediately but non-blocking, so the
            LCP hero heading reaches its real font in parallel with the load
            rather than at requestIdleCallback. Must run after HeadContent so the
            preload links are already in the DOM. */}
        <script dangerouslySetInnerHTML={{ __html: FONT_APPLY_SCRIPT }} />
        {/* noscript fallback for the font + manifest injection */}
        <noscript>
          {FONT_URLS.map((u) => (
            <link key={u} rel="stylesheet" href={u} crossOrigin="anonymous" />
          ))}
          <link rel="manifest" href="/manifest.json" />
        </noscript>
      </head>
      <body>
        {children}
        {/* Inject fonts + manifest at end-of-body so they don't block initial
            render. By the time this script runs the page has already painted. */}
        <script dangerouslySetInnerHTML={{ __html: DEFERRED_HEAD_SCRIPT }} />
        {/* Every page hydrates. The home page briefly shipped without this
            (zero-JS for a Lighthouse TBT win, 2026-06-28) but its menu, sign-in
            buttons, session avatar and fade-in images all need React — on
            mobile it was a page you couldn't tap. Removed 2026-10-09. */}
        <Scripts />
      </body>
    </html>
  )
}
